/**
 * Shared helpers for AI planner seating read/write tools.
 */

import { guestSeatingForPlan, type Guest, type Table } from "@amazing-wedding/shared-types";
import type { ToolContext } from "../types";
import {
  effectiveSeatMap,
  parseOccupantKey,
  findFirstEmptySeatForParty,
  countOccupiedSeats,
  usableCapacity,
} from "./seat-map";

export function guestsById(guests: Guest[]): Record<string, Guest> {
  return Object.fromEntries(guests.map((g) => [g.id, g]));
}

export function guestDisplayName(g: Guest): string {
  return `${g.firstName} ${g.lastName}`.trim();
}

export function tableHeadCount(table: Table, byId: Record<string, Guest>): number {
  const seatMap = effectiveSeatMap(table, byId);
  return countOccupiedSeats(table, seatMap);
}

/** Convert 1-based seat number (user-facing) to 0-based index. */
export function seatNumberToIndex(seatNumber: number): number {
  if (!Number.isInteger(seatNumber) || seatNumber < 1) {
    throw new Error(`Invalid seatNumber ${seatNumber}. Seat numbers start at 1.`);
  }
  return seatNumber - 1;
}

/** Convert 0-based index to 1-based seat number, or null if not seated. */
export function seatIndexToNumber(index: number | null | undefined): number | null {
  if (index == null || index < 0) return null;
  return index + 1;
}

export function findGuestSeatIndex(seatMap: (string | null)[], guestId: string): number {
  return seatMap.findIndex((key) => {
    if (!key) return false;
    try {
      return parseOccupantKey(key).guestId === guestId && parseOccupantKey(key).type === "host";
    } catch {
      return false;
    }
  });
}

/**
 * First seat where the whole party fits (host + companions, respecting hidden seats).
 * Returns 0-based index or null.
 */
export function findFirstEmptySeat(
  seatMap: (string | null)[],
  guest: Guest,
  hiddenSeats: number[],
): number | null {
  return findFirstEmptySeatForParty(seatMap, guest, hiddenSeats);
}

export function occupantLabel(key: string, byId: Record<string, Guest>): string {
  const ref = parseOccupantKey(key);
  const host = byId[ref.guestId];
  if (!host) return key;

  const name = guestDisplayName(host);
  if (ref.type === "plusOne") {
    const plusName = host.plusOne
      ? `${host.plusOne.firstName} ${host.plusOne.lastName}`.trim()
      : null;
    return plusName ? `${name} (+1: ${plusName})` : `${name} (+1)`;
  }
  if (ref.type === "child") {
    const child = host.children?.find((c) => c.id === ref.childId);
    const childName = child?.firstName?.trim() || "?";
    return `${name} (dziecko: ${childName})`;
  }
  return name;
}

export interface TableSeatSummary {
  seatNumber: number;
  occupant: string | null;
  guestId: string | null;
  hidden: boolean;
}

export interface TableListEntry {
  id: string;
  name: string;
  shape: Table["shape"];
  capacity: number;
  isVip: boolean;
  headCount: number;
  freeSeats: number;
  seats: TableSeatSummary[];
}

export function buildTableListEntry(table: Table, guests: Guest[]): TableListEntry {
  const byId = guestsById(guests);
  const seatMap = effectiveSeatMap(table, byId);
  const seated = tableHeadCount(table, byId);
  const hidden = new Set(table.hiddenSeats ?? []);

  const seats: TableSeatSummary[] = seatMap.map((key, index) => {
    if (!key) {
      return {
        seatNumber: index + 1,
        occupant: null,
        guestId: null,
        hidden: hidden.has(index),
      };
    }
    const ref = parseOccupantKey(key);
    return {
      seatNumber: index + 1,
      occupant: occupantLabel(key, byId),
      guestId: ref.type === "host" ? ref.guestId : null,
      hidden: hidden.has(index),
    };
  });

  return {
    id: table.id,
    name: table.name,
    shape: table.shape,
    capacity: table.capacity,
    isVip: table.isVip,
    headCount: seated,
    freeSeats: Math.max(0, usableCapacity(table) - seated),
    seats,
  };
}

export function requireSeatingPlanId(ctx: ToolContext): string {
  const planId = ctx.seatingPlanId();
  if (!planId) throw new Error("Brak aktywnego planu stołów.");
  return planId;
}

export function guestTableInPlan(ctx: ToolContext, guest: Guest): string | null {
  const w = ctx.wedding();
  return guestSeatingForPlan(guest, ctx.seatingPlanId(), w.legacySeatingPlanId).tableId;
}

export function guestSeatNumber(
  guest: Guest,
  tables: Table[],
  guests: Guest[],
  planId: string | null | undefined,
  legacyPlanId?: string | null,
): number | null {
  const tableId = guestSeatingForPlan(guest, planId, legacyPlanId).tableId;
  if (!tableId) return null;
  const table = tables.find((t) => t.id === tableId);
  if (!table) return null;
  const byId = guestsById(guests);
  const seatMap = effectiveSeatMap(table, byId);
  const idx = findGuestSeatIndex(seatMap, guest.id);
  if (idx >= 0) return idx + 1;
  const stored = guestSeatingForPlan(guest, planId, legacyPlanId).seatNumber;
  return seatIndexToNumber(stored);
}

export function tableName(ctx: { tables: () => Table[] }, tableId: string): string {
  return ctx.tables().find((t) => t.id === tableId)?.name ?? tableId;
}

export function guestName(ctx: { guests: () => Guest[] }, guestId: string): string {
  const g = ctx.guests().find((x) => x.id === guestId);
  return g ? guestDisplayName(g) : guestId;
}

export {
  defaultTablePosition,
  computeTablePositions,
  type DefaultTablePositionOptions,
  type TableLayoutOptions,
  type TablePosition,
} from "./table-layout";
